// Play short source-timecode ranges without downloading a multi-GB interview.
// Unlike decodeAudioData(fetch(entireVideo)), Mediabunny performs ranged reads
// and can seek directly to the chosen passage.
export const MAX_PREVIEW_DECODE_SECONDS=90;

export function assembleRangedAudio(chunks,start,end,context){
 if(!Array.isArray(chunks)||!chunks.length)throw new Error('No decodable audio samples in this passage.');
 const first=chunks.find(item=>item?.buffer?.numberOfChannels>0);
 if(!first)throw new Error('No audio samples were returned for this passage.');
 const sampleRate=first.buffer.sampleRate,channels=first.buffer.numberOfChannels;
 if(!Number.isFinite(sampleRate)||sampleRate<=0||sampleRate>192000||!Number.isInteger(channels)||channels<1||channels>8)
  throw new Error('Unsupported audio track layout for precise preview.');
 const frames=Math.max(1,Math.round((end-start)*sampleRate));
 const output=context.createBuffer(channels,frames,sampleRate);
 const write=Array.from({length:channels},(_,index)=>output.getChannelData(index));
 let copied=0;
 for(const chunk of chunks){
  const buffer=chunk?.buffer,timestamp=chunk?.timestamp;
  if(!buffer||!Number.isFinite(timestamp)||buffer.sampleRate!==sampleRate||buffer.numberOfChannels!==channels)
   throw new Error('Audio track format changed inside the selected passage.');
  const offset=Math.round((timestamp-start)*sampleRate);
  const from=Math.max(0,-offset),to=Math.max(0,offset);
  const count=Math.min(buffer.length-from,frames-to);
  if(count<=0)continue;
  for(let channel=0;channel<channels;channel++){
   write[channel].set(buffer.getChannelData(channel).subarray(from,from+count),to);
  }
  copied+=count;
 }
 if(!copied)throw new Error('No audio decoded at the selected source timecodes.');
 return output;
}

export async function decodeRangedMediaAudio(url,start,end,context){
 if(!url||!context||!Number.isFinite(start)||!Number.isFinite(end)||start<0||end<=start)
  throw new Error('Invalid audio passage range.');
 if(end-start>MAX_PREVIEW_DECODE_SECONDS)
  throw new Error('Long passage uses direct browser playback instead of pre-decoding audio.');
 const {Input,ALL_FORMATS,UrlSource,AudioBufferSink}=await import('mediabunny');
 const input=new Input({formats:ALL_FORMATS,
  source:new UrlSource(url,{maxCacheSize:4*1024*1024})});
 try{
  if(!await input.canRead())throw new Error('The media container cannot be decoded precisely.');
  const track=await input.getPrimaryAudioTrack();
  if(!track)throw new Error('This interview has no audio track.');
  if(!await track.canDecode())throw new Error('This audio codec cannot be decoded precisely.');
  const sink=new AudioBufferSink(track),chunks=[];
  for await(const item of sink.buffers(start,end))chunks.push(item);
  return assembleRangedAudio(chunks,start,end,context);
 }finally{await input.dispose?.();}
}
